import React from "react";
import { FormattedMessage } from "react-intl";
import MapPin from "../../assets/illustrations/map-pin.svg";

import olms from "ol-mapbox-style";
import "ol/ol.css";
import { fromLonLat } from "ol/proj.js";
import { Map, View, Feature } from "ol";
import mediaQueries, { between } from "../../styles/mediaQueries";
import { EventInfo } from "../../types";
import { Button } from "@hooplab/ui-components";
import * as styles from "./styles";
import { Point } from "ol/geom";
import { Style } from "ol/style";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import { ReactComponent as IconExternal } from "../../assets/icons/external.svg";
import { Icon } from "ol/style";
import { EventMap } from "@hooplab/ui-components";
import MediaQuery from "react-responsive";

const openGoogleMaps = (event: EventInfo) => {
  const latitude = event.mapData.latitude;
  const longitude = event.mapData.longitude;
  window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`);
};

const LocationInfo: React.FC<{ event: EventInfo }> = ({ event }) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const mapElement = React.useRef<HTMLDivElement>(null);

  const callback = React.useCallback(
    (entries: any) => {
      if (
        entries.map((e: IntersectionObserverEntry) => e.isIntersecting).includes(true) &&
        !isLoaded
      ) {
        if (event.mapData.latitude && event.mapData.longitude) {
          setIsLoaded(true);
          const merc = fromLonLat([
            parseFloat(event.mapData.longitude),
            parseFloat(event.mapData.latitude),
          ]);
          const circleFeature = new Feature({
            geometry: new Point(merc),
          });
          circleFeature.setStyle(
            new Style({
              image: new Icon({
                opacity: 1,
                src: MapPin,
                scale: 1.3,
              }),
            }),
          );
          const styleJson = `https://api.maptiler.com/maps/f7a517d8-ef5d-4a52-a902-26cff6a3cc65/style.json?key=${window.ENVIRONMENT.__MAPTILER_API_KEY__}`;
          const circleVector = new VectorLayer({
            source: new VectorSource({
              features: [circleFeature],
            }),
            zIndex: 1000,
          });
          const initialMap = new Map({
            target: mapElement.current || undefined,
            view: new View({
              center: merc,
              zoom: 10,
            }),
          });
          olms.apply(initialMap, [initialMap, styleJson]).then((map) => {
            (map as Map).addLayer(circleVector);
          });
        }
      }
    },
    [event.mapData.latitude, event.mapData.longitude, isLoaded],
  );

  React.useEffect(() => {
    const observer = new IntersectionObserver(callback, {});
    const cur = mapElement.current;
    if (cur) {
      observer.observe(cur);
    }

    // Clean up the observer when the component unmounts
    return () => {
      if (cur) {
        observer.unobserve(cur);
      }
    };
  }, [callback]);

  const renderLocationNoMap = (event: EventInfo) => {
    if (event.isLivestream) {
      return (
        <div className={styles.organizerInfoBoxText}>
          <span>
            <FormattedMessage id={"screens.Event.howToJoin"} defaultMessage={"How to join"} />
          </span>
          <span>
            <FormattedMessage
              id={"screens.Event.seeTicketDigital"}
              defaultMessage={"All attendees get an invite link when ordering their ticket."}
            />
          </span>
        </div>
      );
    } else if (event.address && event.location) {
      return (
        <div className={styles.organizerInfoBoxText}>
          <span>
            <FormattedMessage id={"screens.Event.address"} defaultMessage={"Address"} />
          </span>
          <div className={styles.address}>
            <span className={styles.addressText}>{event.location}</span>
            <span className={styles.infoText}>
              {event.address.streetAddress ? event.address.streetAddress : null}
            </span>
            <span className={styles.infoText}>
              {event.address.postalArea && event.address.postalCode
                ? event.address.postalCode + " " + event.address.postalArea
                : null}
            </span>
          </div>
        </div>
      );
    } else if (event.location) {
      return (
        <div className={styles.organizerInfoBoxText}>
          <span>
            <FormattedMessage id={"screens.Event.place"} defaultMessage={"Place"} />
          </span>
          <span>{event.location}</span>
        </div>
      );
    } else {
      return (
        <div className={styles.organizerInfoBoxText}>
          <span>
            <FormattedMessage id={"screens.Event.noLocation"} defaultMessage={"No location"} />
          </span>
          <span>
            <FormattedMessage
              id={"screens.Event.organizerHasNotSetLocation"}
              defaultMessage={"The organizer has not supplied a location for this event."}
            />
          </span>
        </div>
      );
    }
  };

  return (
    <EventMap
      hasMap={event.mapData.mapImage}
      map={
        <div className={styles.mapWrapper}>
          <MediaQuery {...between({ minWidth: 0 }, mediaQueries.mobile)}>
            <div ref={mapElement} className={styles.mapImage} style={{ height: 220 }}></div>
          </MediaQuery>
          <MediaQuery {...between(mediaQueries.tablet, mediaQueries.tablet)}>
            <div ref={mapElement} className={styles.mapImage} style={{ height: 280 }}></div>
          </MediaQuery>
          <MediaQuery {...between(mediaQueries.desktopSmall, { maxWidth: 99999 })}>
            <div ref={mapElement} className={styles.mapImage} style={{ height: 280 }}></div>
          </MediaQuery>
        </div>
      }
    >
      {event.mapData.mapImage ? (
        <React.Fragment>
          {(event.address || event.location) && (
            <div className={styles.organizerInfoBoxText}>
              <span>
                <FormattedMessage id={"screens.Event.address"} defaultMessage={"Address"} />
              </span>
              <div className={styles.address}>
                <span className={styles.addressText}>{event.location}</span>
                {event.address?.streetAddress ? (
                  <span className={styles.infoText}>{event.address.streetAddress}</span>
                ) : null}
                {event.address?.postalArea && event.address.postalCode ? (
                  <span className={styles.infoText}>
                    {event.address.postalCode + " " + event.address.postalArea}
                  </span>
                ) : null}
              </div>
            </div>
          )}
          <div data-nosnippet>
            <Button className={styles.mapButton} onClick={() => openGoogleMaps(event)}>
              <FormattedMessage id={"screens.Event.showInMaps"} defaultMessage={"Show on map"} />{" "}
              <IconExternal className="ml-3" height={"14px"} />
            </Button>
          </div>
        </React.Fragment>
      ) : (
        <div className={styles.infoBoxContent}>{renderLocationNoMap(event)}</div>
      )}
    </EventMap>
  );
};

export default LocationInfo;
